/** XeAgents.ai**/
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { StepCard } from "./StepCard";
import { AIContentField } from "./AIContentField";
import type { AIContentState, ContentOrigin, SalesContentField, TemplateVariable } from "./types";

const VALUE_PROPOSITION_PLACEHOLDER =
  "24/7 AI voice agents that handle customer conversations in multiple languages, alongside the systems you already use.";

const FEATURES_PLACEHOLDER = `• AI voice agents for sales, support and collections
• Supports multiple languages
• CRM integration
• Automated outbound calling
• Real-time call analytics`;

const OBJECTIONS_PLACEHOLDER = `Objection: Is it reliable?
Response: AI agents are designed for high availability.

Objection: Is it expensive?
Response: It can reduce the operational cost of repetitive customer conversations.`;

/**
 * The section the call actually turns on.
 *
 * These three are what a prospect hears in the first thirty seconds, which is
 * why they are mandatory and why each one carries its own Regenerate: a value
 * proposition that came out wrong is worth one more attempt on its own, not a
 * re-run of the whole page that would take the other two with it.
 *
 * Under them sit the fields THIS template speaks that the form does not ask for
 * by name - proof point, monthly price, practice area. They are in the same card
 * because they do the same job: they are the words the agent says. Splitting
 * them into a separate "template variables" section is what made the old page
 * read as a form with an unexplained appendix.
 */
export function SalesContent({
  content,
  onChange,
  onRewrite,
  onGenerateAll,
  error,
  canGenerate,
  generating,
  extraVariables,
  raw,
  onValueChange,
  onBlur,
  variableOrigins,
  onRewriteVariable,
}: {
  content: Record<SalesContentField, AIContentState>;
  onChange: (field: SalesContentField, value: string) => void;
  onRewrite: (field: SalesContentField, instruction: string) => void;
  onGenerateAll: () => void;
  error: (key: string) => string | undefined;
  canGenerate: boolean;
  generating: boolean;
  extraVariables: TemplateVariable[];
  raw: (key: string) => string;
  onValueChange: (key: string, value: string) => void;
  onBlur: (key: string) => void;
  /** Where each template value came from: the model, or the user. */
  variableOrigins: Record<string, ContentOrigin>;
  onRewriteVariable: (key: string, instruction: string) => void;
}) {
  const nothingWritten = Object.values(content).every(field => !field.value.trim());

  return (
    <StepCard
      step={3}
      title="Key Sales Content"
      description="AI-generated from your website and product details. Review and edit anything before saving."
      action={
        nothingWritten ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onGenerateAll}
            disabled={!canGenerate || generating}
            data-testid="button-generate-all"
          >
            <Sparkles className="mr-2 h-3.5 w-3.5" />
            Generate all
          </Button>
        ) : undefined
      }
    >
      <AIContentField
        id="valueProposition"
        label="Unique Value Proposition"
        required
        hint="Spoken word-for-word in the opening pitch, right after your product name. Keep it short — every extra word is one the prospect waits through before being asked anything."
        placeholder={VALUE_PROPOSITION_PLACEHOLDER}
        state={content.valueProposition}
        error={error('valueProposition')}
        rows={3}
        onChange={(next) => onChange('valueProposition', next)}
        onRewrite={(instruction) => onRewrite('valueProposition', instruction)}
        canRewrite={canGenerate}
      />

      <AIContentField
        id="keyFeatures"
        label="Key Features / Benefits"
        required
        hint="Not read out as a list. These become instructions that rewrite how the agent describes the product, and the first line is what it leads with."
        placeholder={FEATURES_PLACEHOLDER}
        state={content.keyFeatures}
        error={error('keyFeatures')}
        rows={5}
        onChange={(next) => onChange('keyFeatures', next)}
        onRewrite={(instruction) => onRewrite('keyFeatures', instruction)}
        canRewrite={canGenerate}
      />

      <AIContentField
        id="objections"
        label="Common Objections & Responses"
        required
        hint="Each pair rewrites the matching objection step in the flow — price, an incumbent supplier, timing, trust. Up to five are used, so cover the ones you actually hear."
        placeholder={OBJECTIONS_PLACEHOLDER}
        state={content.objections}
        error={error('objections')}
        rows={7}
        onChange={(next) => onChange('objections', next)}
        onRewrite={(instruction) => onRewrite('objections', instruction)}
        canRewrite={canGenerate}
      />

      {/* Written by the same pass that writes the three above, and carrying the
          same provenance badge and rewrite dialog. They used to be plain boxes:
          the page would finish a polished pitch and leave five required fields
          empty underneath it, each one a fragment of the very sentences it had
          just written. Nobody fills five empty boxes by hand at the end of a
          form, so flows shipped saying "{{proof_point}}" out loud. */}
      {extraVariables.length > 0 && (
        <div className="space-y-4 border-t pt-5">
          <div>
            <h3 className="text-sm font-medium">Values this template speaks</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              This script names them directly. A field marked required has no generic substitute — left empty it is
              read out with the braces still on.
            </p>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            {extraVariables.map((variable) => (
              <AIContentField
                key={variable.key}
                id={`var-${variable.key}`}
                label={variable.key.replace(/_/g, " ")}
                labelClassName="capitalize"
                required={variable.required}
                hint={variable.description}
                state={{
                  value: raw(variable.key),
                  origin: variableOrigins[variable.key] ?? 'empty',
                }}
                error={error(variable.key)}
                rows={2}
                onChange={(next) => onValueChange(variable.key, next)}
                onBlur={() => onBlur(variable.key)}
                onRewrite={(instruction) => onRewriteVariable(variable.key, instruction)}
                canRewrite={canGenerate}
              />
            ))}
          </div>
        </div>
      )}
    </StepCard>
  );
}
